import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';

class AppInsightsService {
  private appInsights: ApplicationInsights | null = null;
  private initialized = false;

  /**
   * Initialize Application Insights SDK.
   */
  init(connectionString: string): void {
    if (this.initialized) {
      console.warn('[AppInsights] Already initialized');
      return;
    }

    if (!connectionString) {
      console.warn('[AppInsights] No connection string provided. Telemetry disabled.');
      return;
    }

    try {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString,
          enableAutoRouteTracking: true,
          // Enable W3C traceparent/tracestate headers on every API call so
          // requests are linked to backend telemetry in Application Map.
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          disableFetchTracking: false,
          disableAjaxTracking: false,
          correlationHeaderExcludedDomains: [
            '*.mixpanel.com',
            '*.contentsquare.net',
            '*.google-analytics.com',
          ],
        },
      });

      this.appInsights.loadAppInsights();

      // Set cloud role for Application Map
      this.appInsights.addTelemetryInitializer((envelope) => {
        if (envelope.tags) {
          envelope.tags['ai.cloud.role'] = 'facturacion-portal';
        }
      });

      // Enriquecer toda la telemetría con el userId/email del usuario autenticado.
      // Permite filtrar en Transaction Search por usuario sin instrumentar cada llamada.
      this.appInsights.addTelemetryInitializer((envelope) => {
        try {
          const authRaw = localStorage.getItem('auth-storage');
          if (authRaw) {
            const auth = JSON.parse(authRaw);
            const userId = auth?.state?.user?.id;
            const email = auth?.state?.user?.email;
            if (userId && envelope.data?.baseData) {
              envelope.data.baseData.properties = {
                ...envelope.data.baseData.properties,
                userId: String(userId),
                userEmail: email || 'unknown',
              };
            }
          }
        } catch { /* no romper telemetría */ }
      });

      this.setupConsoleInterceptor();

      this.initialized = true;
      console.log('[AppInsights] Initialized successfully');
    } catch (error) {
      console.error('[AppInsights] Failed to initialize:', error);
    }
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  trackEvent(name: string, properties?: Record<string, string>, measurements?: Record<string, number>): void {
    if (!this.appInsights) return;
    this.appInsights.trackEvent({ name, properties, measurements });
  }

  trackException(error: Error | unknown, properties?: Record<string, string>): void {
    if (!this.appInsights) return;
    const exception = error instanceof Error ? error : new Error(String(error));
    this.appInsights.trackException({
      exception,
      severityLevel: SeverityLevel.Error,
      properties,
    });
  }

  trackPageView(name?: string, properties?: Record<string, string>): void {
    if (!this.appInsights) return;
    this.appInsights.trackPageView({ name, properties });
  }

  trackMetric(name: string, average: number, properties?: Record<string, string>): void {
    if (!this.appInsights) return;
    this.appInsights.trackMetric({ name, average }, properties);
  }

  trackTrace(message: string, severity: SeverityLevel = SeverityLevel.Information, properties?: Record<string, string>): void {
    if (!this.appInsights) return;
    this.appInsights.trackTrace({ message, severityLevel: severity, properties });
  }

  setAuthenticatedUser(userId: string, accountId?: string): void {
    if (!this.appInsights) return;
    this.appInsights.setAuthenticatedUserContext(userId, accountId, true);
  }

  clearAuthenticatedUser(): void {
    if (!this.appInsights) return;
    this.appInsights.clearAuthenticatedUserContext();
  }

  // Intercept console.error and console.warn so they appear as Traces in App Insights.
  // A re-entrancy guard prevents infinite loops if the SDK itself logs an error.
  private setupConsoleInterceptor(): void {
    let intercepting = false;
    const ai = this.appInsights!;

    const originalError = console.error.bind(console);
    console.error = (...args: unknown[]) => {
      originalError(...args);
      if (!intercepting) {
        intercepting = true;
        try {
          const message = args.map(a => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ');
          ai.trackTrace({ message, severityLevel: SeverityLevel.Error });
        } finally {
          intercepting = false;
        }
      }
    };

    const originalWarn = console.warn.bind(console);
    console.warn = (...args: unknown[]) => {
      originalWarn(...args);
      if (!intercepting) {
        intercepting = true;
        try {
          const message = args.map(a => (typeof a === 'string' ? a : JSON.stringify(a))).join(' ');
          ai.trackTrace({ message, severityLevel: SeverityLevel.Warning });
        } finally {
          intercepting = false;
        }
      }
    };
  }
}

export const appInsightsService = new AppInsightsService();
export default appInsightsService;
